/**
 * Responsive breakpoint system
 *
 * Single source of truth for all responsive behavior.
 * CSS media queries and JS hooks must use these values.
 *
 * Strict two-tier model (PDR 008 D3, amended 2026-06-12; see
 * docs/refactor/css-god-file-decomposition-plan.md §11):
 * - compactMax: 767px — the mobile tier (≤767px): sidebar drawer, mobile
 *   header, fullscreen modals, compact module adaptations. JS hooks
 *   (useIsCompactLayout) and CSS `(max-width: 767px)` queries bind here.
 * - desktopSplitMin: 768px — the desktop tier (≥768px). The split layout
 *   (rail + module + chat side-by-side) IS the desktop layout; the Daily
 *   Brief dock placements live here too. There is no intermediate tier:
 *   desktop panes adapt to narrow widths via `@container` queries, not
 *   viewport tiers. At narrow desktop the chat column clamps at its 360px
 *   floor (see --chat-split-right-col in ChatBox.css).
 *
 * The two constants are the two sides of the SAME 767/768 boundary —
 * `MQ.compact` and `MQ.desktopSplit` are mutually exclusive and exhaustive.
 */

export const BREAKPOINTS = {
  compactMax: 767,
  desktopSplitMin: 768,
  /**
   * Min viewport width at which the EXPANDED sidebar rail still leaves the
   * chat its full 680px lane. Below this the rail auto-collapses to icon-only
   * (SidebarRail) so the chat keeps 680px down to the compactMax mobile cut.
   * Derivation: 220 expanded rail (--rail-expanded-width, SidebarRail.css)
   * + 16 themed rail float (nordic/atelier) + 680 chat lane
   * (--chat-lane-max-width, ChatBox.css) + 28 desk gutters ≈ 944.
   * Keep in sync with those CSS values.
   *
   * NOT a styling tier: this is layout-geometry (available-space) adaptation
   * — the natural-squeeze direction PDR 008 D3 (amended) endorses — so it is
   * exempt from the two-tier constants rule by name in guard-css-tiers.
   * Follow-up: convert to a container/ResizeObserver measure so the constant
   * can retire.
   */
  railExpandedMin: 944,
} as const;

export const MQ = {
  compact: `(max-width: ${BREAKPOINTS.compactMax}px)`,
  /** The desktop split view (rail + module + chat) is live at/above this width. */
  desktopSplit: `(min-width: ${BREAKPOINTS.desktopSplitMin}px)`,
} as const;
